import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { useFormSchema } from './hooks'
import { SectionListPanel } from './components/SectionListPanel'
import { SectionEditorWithTabs } from './components/SectionEditorWithTabs'
import { useSectionFormSheet } from './components/SectionFormSheet'
import { useFieldFormSheet } from './components/FieldFormSheet'
import { AccessPolicyPanel } from './components/AccessPolicyPanel'
import { FormBuilderObjectTypeTabs } from './components/FormBuilderObjectTypeTabs'

export function FormSchemaEditorPage() {
  const { objectType } = useParams<{ objectType: string }>()
  const ot = objectType ?? 'contract'
  const schemaQ = useFormSchema(ot)
  const [selectedCode, setSelectedCode] = useState<string | null>(null)

  // A brand-new object type (e.g. 'product' before any section exists) returns 404.
  // Treat that as an empty schema so the first section can be created from scratch.
  const is404 = (schemaQ.error as { response?: { status?: number } } | null)?.response?.status === 404
  const schema = schemaQ.data ?? (is404 ? { objectType: ot, sections: [], fields: [], placements: [] } : undefined)

  const sectionSheet = useSectionFormSheet(ot)
  const fieldSheet = useFieldFormSheet(ot, schema)

  useEffect(() => {
    if (!schema) return
    if (selectedCode && schema.sections.some(s => s.code === selectedCode)) return
    setSelectedCode(schema.sections[0]?.code ?? null)
  }, [schema, selectedCode])

  if (schemaQ.isLoading) return <div className="p-4">Ładowanie schematu...</div>
  if (schemaQ.error && !is404) return <div className="p-4 text-red-600">Błąd: {String(schemaQ.error)}</div>
  if (!schema) return null

  const selectedSection = schema.sections.find(s => s.code === selectedCode) ?? null

  return (
    <div className="flex flex-col w-full h-full">
      <header className="px-4 py-3 border-b">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-lg font-semibold">Sekcje formularzy</h1>
            <p className="text-xs text-slate-500">
              Globalny katalog sekcji + pól. Podpinasz je do produktów w{' '}
              <Link to="/settings/configurations" className="text-sky-700 underline">Produkty</Link>.
            </p>
          </div>
          <FormBuilderObjectTypeTabs active={ot} />
        </div>
      </header>

      <Tabs defaultValue="schema" className="flex-1 flex flex-col gap-0 overflow-hidden">
        <TabsList className="rounded-none w-full justify-start gap-2 px-4 border-b bg-white h-auto">
          <TabsTrigger value="schema">Sekcje i pola</TabsTrigger>
          <TabsTrigger value="access">Uprawnienia</TabsTrigger>
        </TabsList>

        <TabsContent value="schema" className="flex-1 flex overflow-hidden">
          <SectionListPanel
            schema={schema}
            selectedCode={selectedCode}
            onSelect={setSelectedCode}
            onAdd={sectionSheet.openCreate}
            onEdit={sectionSheet.openEdit}
            objectType={ot}
          />
          {selectedSection ? (
            <SectionEditorWithTabs
              schema={schema}
              section={selectedSection}
              objectType={ot}
              onAddField={() => fieldSheet.openCreate(selectedSection.code)}
              onEditField={fieldSheet.openEdit}
            />
          ) : (
            <div className="flex-1 flex items-center justify-center text-sm text-slate-500">
              Wybierz sekcję z listy lub dodaj pierwszą.
            </div>
          )}
        </TabsContent>

        <TabsContent value="access" className="flex-1 overflow-hidden">
          <AccessPolicyPanel objectType={ot} schema={schema} />
        </TabsContent>
      </Tabs>

      {sectionSheet.sheet}
      {fieldSheet.sheet}
    </div>
  )
}
